import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import ForgottenPasswordPageTemplate from "../../components/templates/ForgottenPasswordPageTemplate";
import ForgottenPasswordFormStates from "../../constants/forgotten-password-states";
import { requestResetCode, validateResetCode, getPasswordHints } from "./controller";
import Constants from "../../constants/text-constants";
import RegExConst from "../../constants/regex";
import accountHandler from "../../services/api/accountHandler";
import authenticationHandler from "../../services/api/authenticationHandler";

import { useMst } from "../../hooks/useMst";

const checkObjectStringLengths = (obj) => {
  // Check all elements of an object have length > 0
  let result = true;

  // Blank object passed - false is returned
  if (Object.keys(obj).length === 0) {
    return false;
  }

  for (var key in obj) {
    if (obj.hasOwnProperty(key)) {
      result = result && obj[key].length > 0;
    }
  }
  return result;
}

const checkArrayStringLengths = (array) => {
  // Check any element of an array has string length > 0
  return array.filter((x) => x.trim().length > 0).length > 0 ? true : null;
}

const checkRequiredField = (value) => {
  if (Array.isArray(value)) {
    // check at least one element has a value for submit button to be enabled
    return checkArrayStringLengths(value);
  } else {
    if (typeof value === 'object' && value !== null) {
      return checkObjectStringLengths(value);
    } else {
      // Just a string, check it has length > 0
      return value && value.length > 0 ? true : false;
    }
  }
};

const checkFieldValid = (value, rules) => {
  let valid = null;

  if (rules) {
    //can add other rules here such as required, min and max length
    if (rules.required === true) {
      valid = checkRequiredField(value);
    }

    if (rules.pattern) {
      const validExp = new RegExp(rules.pattern);
      valid = validExp.test(value);
    }
  }
  return valid;
};

const ForgottenPasswordPage = (props) => {
  let location = useLocation();
  const { passwordHintStore } = useMst();

  let name = "";
  if (location.state) {
    name = location.state.passedInUsername;
  }

  const [errorDialog, setErrorDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [username, setUsername] = useState({
    value: name,
    valid: name.length > 0,
    rules: {
      required: true,
    },
  });

  const [email, setEmail] = useState({
    value: "",
    valid: false,
    rules: {
      required: true,
    },
    error: "",
  });

  const [password, setPassword] = useState({
    value: { newPassword: "", newPasswordConfirm: "" },
    valid: false,
    rules: {
      required: true,
    },
    newPasswordError: null,
    newPasswordConfirmError: null,
  });

  const resetPasswordError = () => {
    setPassword((prevState) => ({
      ...prevState,
      newPasswordError: null,
      newPasswordConfirmError: null,
    }));
  }

  const [loading, setLoading] = useState(false);

  const [resetCode, setResetCode] = useState({
    value: Array(6).fill(""),
    error: null,
    rules: {
      required: true,
    },
    validating: false,
  });

  const resetCodeStep = async (code) => {
    let goToNextStep = false;

    setResetCode((prevState) => ({
      ...prevState,
      validating: true,
    }));

    const resp = await validateResetCode(username.value, code.join(""));

    let error = null;
    if (resp && resp.error) {
      error = resp.error;
    } else {

      const res = await getPasswordHints();
      if (res) {
        passwordHintStore.setPasswordHints(res);
      }

      goToNextStep = true;
    }

    setResetCode((prevState) => ({
      ...prevState,
      validating: false,
      error,
    }));

    return goToNextStep;
  };

  const emailStep = async () => {
    let goToNextStep = false;

    // validate email address
    const regEx = new RegExp(RegExConst.EMAIL_VALIDATION);

    const valid = regEx.test(email.value);
    if (!valid) {
      setLoading(false);
      setEmail((prevState) => ({
        ...prevState,
        error: Constants.INVALID_EMAIL,
      }));
      return false;
    }

    setLoading(true);

    const resp = await requestResetCode(username.value, email.value);
    if (!resp) {
      setErrorDialog(true);
    } else {
      goToNextStep = true;
    }
    setLoading(false);
    return goToNextStep;
  }

  const resetPassword = async () => {
    // check password validity here, return false if it fails

    // Check passwords match
    if (password.value.newPassword !== password.value.newPasswordConfirm) {
      setLoading(false);
      setPassword((prevState) => ({
        ...prevState,
        newPasswordError: "",
        newPasswordConfirmError: Constants.PASSWORDS_MUST_MATCH,
      }));
    } else {
      setLoading(true);

      //if its ok, call the API 
      try {
        await accountHandler.setPassword(password.value.newPassword);

        gotoSignIn();
      } catch (e) {
        // an error occured
        setLoading(false);
        setPassword((prevState) => ({
          ...prevState,
          newPasswordError: Constants.PASSWORD_DOESNT_MATCH_CRITERIA,
        }));
      }
    }

    // This is the last step in password reset, so specify that we don't want to move on to another step
    return false;
  }

  const gotoNextStep = async (args) => {
    let goToNextStep = true;

    switch (currentStep) {
      case ForgottenPasswordFormStates.USERNAME:
        break;
      case ForgottenPasswordFormStates.EMAIL:
        goToNextStep = await emailStep();
        break;
      case ForgottenPasswordFormStates.RESET_CODE:
        goToNextStep = await resetCodeStep(args);
        break;
      case ForgottenPasswordFormStates.RESET_PASSWORD:
        goToNextStep = await resetPassword();
        break;
      default:
        console.error("Unknown switch case")
        break;
    }

    if (goToNextStep) {
      setCurrentStep((prevState) => prevState + 1);
    }
  };

  const gotoSignIn = () => {
    // Remove any auth tokens from local storage, and go back to the sigin page
    authenticationHandler.signoutAndRedirectToLogin();
  };

  const setFieldValues = (field, value, setField) => {
    const valid = !field.rules
      ? null
      : checkFieldValid(value, field.rules);

    setField((prevState) => {
      return {
        ...prevState,
        value,
        valid,
      };
    });
  }

  const handleValueChange = (fieldName, value) => {
    switch (fieldName) {
      case "username":
        setFieldValues(username, value, setUsername);
        break;

      case "email":
        setFieldValues(email, value, setEmail);
        // This will blank the error message if you change the contents of email input
        setEmail((prevState) => ({
          ...prevState,
          error: "",
        }));
        break;

      case "resetCode":
        setFieldValues(resetCode, value, setResetCode);
        break;

      case "resetPasswordPassword":
        setFieldValues(password, { ...password.value, newPassword: value }, setPassword);
        // This will blank the error message if you change the contents of either password input
        resetPasswordError();
        break;

      case "resetPasswordPasswordConfirm":
        setFieldValues(password, { ...password.value, newPasswordConfirm: value }, setPassword);
        // This will blank the error message if you change the contents of either password input
        resetPasswordError();
        break;

      default:
        console.error("Unknown switch in handleValueChange");
        break;
    }
  }

  const resetCodeInputs = () => {
    setResetCode((prevState) => ({
      ...prevState,
      value: Array(6).fill(""),
      error: null,
    }));
  };

  const onErrorConfirm = () => {
    setErrorDialog(false);
  }

  return (
    <ForgottenPasswordPageTemplate
      currentStep={currentStep}
      gotoNextStep={gotoNextStep}
      handleValueChange={handleValueChange}
      username={username}
      resetCode={resetCode}
      gotoSignIn={gotoSignIn}
      resetCodeInputs={resetCodeInputs}
      email={email}
      password={password}
      loading={loading}
      showErrorDialog={errorDialog}
      onErrorConfirm={onErrorConfirm}
    />
  );
};

export default ForgottenPasswordPage;
